import { Navigate } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";

import DashboardsDefault from "views/admin/dashboards/church-dashboard";
import EntriesAndExits from "views/admin/dashboards/entries-and-exits";
import FunctionalDashboard from "views/admin/dashboards/functional-dashboard";
import InvestorDemo from "views/admin/dashboards/investor-demo";
import People from "views/admin/people";
import UserProfile from "views/admin/people/profile";
import EditUserPage from "views/admin/people/edit-user-page";
import ServiceScheduler from "views/admin/scheduler";
import SignIn from "views/auth/signin";
import AuthCallback from "views/auth/Callback";
import Occupancy from "views/admin/dashboards/occupancy";
import MemberLocation from "views/admin/member-location";
import Settings from "views/admin/settings";
import VolunteerServices from "views/volunteer/services";
import VolunteerServiceDetails from "views/volunteer/services/details";
import CheckIn from "views/member/check-in";
import CheckOut from "views/member/checkout";
import Registration from "views/member/register";
import PCIntegrationRedirecting from "views/admin/redirecting/pc-integration";
import Insights from "views/admin/Insights";

// Heartbeat (formerly Guest Campaigns) views
import HeartbeatCampaignsList from "views/admin/heartbeat";
import HeartbeatCreateEditCampaign from "views/admin/heartbeat/CreateEditCampaign";
import HeartbeatCampaignDetails from "views/admin/heartbeat/CampaignDetails";
import HeartbeatActionItems from "views/admin/heartbeat-action-items";

// Attendance views
import AttendanceServiceList from "views/attendance/ServiceList";
import AttendanceEntry from "views/attendance/AttendanceEntry";
import AttendanceReports from "views/attendance/Reports";
import AttendanceCharts from "views/attendance/Charts";

// Connect (Link Forge) views
import ConnectDashboard from "views/admin/connect";
import CustomizeProfile from "views/admin/connect/customize";
import LinksManagement from "views/admin/connect/links";
import SocialLinksManagement from "views/admin/connect/social";
import QRCodeGenerator from "views/admin/connect/qr-code";
import VisitorSubmissions from "views/admin/connect/visitors";
import PrayerRequests from "views/admin/connect/prayers";
import DonationsAnalytics from "views/admin/connect/donations";
import ConnectAnalytics from "views/admin/connect/analytics";
import FundsManagement from "views/admin/connect/funds";

// Public Link Hub
import PublicLinkHub from "views/public/link-hub";

// Groups views
import GroupsList from "views/admin/groups";
import CreateEditGroup from "views/admin/groups/CreateEditGroup";
import GroupDetails from "views/admin/groups/GroupDetails";

// Documents (AI-generated artifacts)
import DocumentsPage from "views/admin/documents";

// Staff module
import TeamOrgChart from "views/admin/team";
import TeamCheckIns from "views/admin/team/CheckIns";
import TeamMyCheckIn from "views/admin/team/MyCheckIn";
import TeamMemberProfile from "views/admin/team/MemberProfile";

// Staff and Volunteers modules: one assessment module, parameterized by audience
import AssessmentWeeklyCheckIn from "views/admin/assessment/WeeklyCheckIn";
import AssessmentOverview from "views/admin/assessment/AssessmentOverview";
import AssessmentProfiles from "views/admin/assessment/Profiles";
import AssessmentProfileDetails from "views/admin/assessment/ProfileDetails";
import AssessmentRoleFit from "views/admin/assessment/RoleFit";
import AssessmentSettings from "views/admin/assessment/Settings";

// Pathways views
import PathwaysDashboard from "views/pathways/dashboard";
import PathwaysOnboarding from "views/pathways/onboarding";

import {
  AttendanceIcon,
  ConnectIcon,
  DashboardIcon,
  DocumentsIcon,
  GroupsIcon,
  HeartbeatIcon,
  InsightsIcon,
  MemberLocationIcon,
  PathwaysIcon,
  PeopleIcon,
  SchedulingIcon,
  SettingsIcon,
  StaffIcon,
  TeamIcon,
  VolunteersIcon,
} from "components/icons/nav";

export const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) {
    // user is not authenticated
    return <Navigate to="/auth/signin" replace />;
  }
  return children;
};

export const PathwayGuard = ({ children }) => {
  const { user } = useAuth();

  // If pathway user hasn't completed onboarding, redirect to onboarding
  if (user?.customerStatus === "pathway" && !user?.pathwaysOnboardingCompleted) {
    return <Navigate to="/admin/pathways/onboarding" replace />;
  }

  // If pathway user is on the main dashboard, redirect to pathways dashboard
  if (user?.customerStatus === "pathway" && window.location.pathname === "/admin/dashboard") {
    return <Navigate to="/admin/pathways/dashboard" replace />;
  }

  return children;
};

/**
 * Route table shared by the layouts, the sidebar and the navbar breadcrumb.
 *
 * - `name`: the single label for a destination (sidebar item and breadcrumb).
 * - `section`: top-level admin routes only; groups sidebar items under a small
 *   heading. Consecutive routes with the same section render as one group.
 * - `hideInSidebar`: routed but not listed in the sidebar.
 * - `fullscreen`: rendered by the admin layout without sidebar, navbar or footer.
 */
export const routes = [
  // Full-screen onboarding wizard (no admin chrome)
  {
    name: "Pathways Onboarding",
    layout: "/admin",
    path: "/pathways/onboarding",
    component: <PathwaysOnboarding />,
    hideInSidebar: true,
    fullscreen: true,
  },

  // ── Overview ──
  {
    name: "Dashboard",
    section: "Overview",
    icon: <DashboardIcon />,
    layout: "/admin",
    path: "/dashboard",
    component: <DashboardsDefault />,
    collapse: true,
    items: [
      {
        name: "Test Dashboard",
        layout: "/admin",
        path: "/dashboard/test",
        component: <FunctionalDashboard />,
        hideInSidebar: true,
      },
      {
        name: "Investor Demo",
        layout: "/admin",
        path: "/dashboard/demo",
        component: <InvestorDemo />,
        hideInSidebar: true,
      },
      {
        name: "Entries and Exits",
        layout: "/admin",
        path: "/dashboard/entries-and-exits",
        component: <EntriesAndExits />,
        hideInSidebar: true,
      },
      {
        name: "Occupancy",
        layout: "/admin",
        path: "/dashboard/occupancy",
        component: <Occupancy />,
        hideInSidebar: true,
      },
    ],
  },
  {
    name: "Insights",
    section: "Overview",
    icon: <InsightsIcon />,
    layout: "/admin",
    path: "/insights",
    component: <Insights />,
  },
  // Available to every tier, including Pathways (free) customers
  {
    name: "Pathways",
    section: "Overview",
    icon: <PathwaysIcon />,
    layout: "/admin",
    path: "/pathways/dashboard",
    component: <PathwaysDashboard />,
  },

  // ── Care ──
  {
    name: "Heartbeat",
    section: "Care",
    icon: <HeartbeatIcon />,
    layout: "/admin",
    path: "/heartbeat",
    component: <HeartbeatCampaignsList />,
    collapse: true,
    items: [
      {
        name: "Campaigns",
        layout: "/admin",
        path: "/heartbeat",
        component: <HeartbeatCampaignsList />,
      },
      {
        name: "Action Items",
        layout: "/admin",
        path: "/heartbeat/action-items",
        component: <HeartbeatActionItems />,
      },
      {
        name: "Create Campaign",
        layout: "/admin",
        path: "/heartbeat/new",
        component: <HeartbeatCreateEditCampaign />,
        hideInSidebar: true,
      },
      {
        name: "Campaign Details",
        layout: "/admin",
        path: "/heartbeat/:id",
        component: <HeartbeatCampaignDetails />,
        hideInSidebar: true,
      },
      {
        name: "Edit Campaign",
        layout: "/admin",
        path: "/heartbeat/:id/edit",
        component: <HeartbeatCreateEditCampaign />,
        hideInSidebar: true,
      },
    ],
  },
  {
    name: "Member Location",
    section: "Care",
    icon: <MemberLocationIcon />,
    layout: "/admin",
    path: "/member-location",
    component: <MemberLocation />,
  },

  // ── People & Teams ──
  {
    name: "People",
    section: "People & Teams",
    icon: <PeopleIcon />,
    layout: "/admin",
    path: "/people",
    component: <People />,
    collapse: true,
    items: [
      {
        name: "Profile",
        layout: "/admin",
        path: "/people/:id/profile",
        component: <UserProfile />,
        hideInSidebar: true,
      },
      {
        name: "Details",
        layout: "/admin",
        path: "/people/:id",
        component: <People />,
        hideInSidebar: true,
      },
      {
        name: "Edit Profile",
        layout: "/admin",
        path: "/people/:id/edit",
        component: <EditUserPage />,
        hideInSidebar: true,
      },
    ],
  },
  {
    name: "Groups",
    section: "People & Teams",
    icon: <GroupsIcon />,
    layout: "/admin",
    path: "/groups",
    component: <GroupsList />,
    collapse: true,
    items: [
      {
        name: "Create Group",
        layout: "/admin",
        path: "/groups/new",
        component: <CreateEditGroup />,
        hideInSidebar: true,
      },
      {
        name: "Group Details",
        layout: "/admin",
        path: "/groups/:id",
        component: <GroupDetails />,
        hideInSidebar: true,
      },
      {
        name: "Edit Group",
        layout: "/admin",
        path: "/groups/:id/edit",
        component: <CreateEditGroup />,
        hideInSidebar: true,
      },
    ],
  },
  // Org chart + cascading weekly check-ins
  {
    name: "Team",
    section: "People & Teams",
    icon: <TeamIcon />,
    layout: "/admin",
    path: "/team",
    component: <TeamOrgChart />,
    collapse: true,
    items: [
      {
        name: "Org Chart",
        layout: "/admin",
        path: "/team",
        component: <TeamOrgChart />,
      },
      {
        name: "Check-Ins",
        layout: "/admin",
        path: "/team/check-ins",
        component: <TeamCheckIns />,
      },
      {
        name: "My Check-In",
        layout: "/admin",
        path: "/team/check-in",
        component: <TeamMyCheckIn />,
      },
      {
        name: "Member Profile",
        layout: "/admin",
        path: "/team/members/:id",
        component: <TeamMemberProfile />,
        hideInSidebar: true,
      },
    ],
  },
  {
    name: "Staff",
    section: "People & Teams",
    icon: <StaffIcon />,
    layout: "/admin",
    path: "/staff",
    component: <AssessmentWeeklyCheckIn audience="staff" key="staff" />,
    collapse: true,
    items: [
      {
        name: "Weekly Check-In",
        layout: "/admin",
        path: "/staff",
        component: <AssessmentWeeklyCheckIn audience="staff" key="staff" />,
      },
      {
        name: "Assessment",
        layout: "/admin",
        path: "/staff/staff-assessment",
        component: <AssessmentOverview audience="staff" key="staff" />,
      },
      {
        name: "Profiles",
        layout: "/admin",
        path: "/staff/people",
        component: <AssessmentProfiles audience="staff" key="staff" />,
      },
      {
        name: "Profile Details",
        layout: "/admin",
        path: "/staff/people/:id",
        component: <AssessmentProfileDetails audience="staff" key="staff" />,
        hideInSidebar: true,
      },
      {
        name: "Role Fit",
        layout: "/admin",
        path: "/staff/role-fit",
        component: <AssessmentRoleFit audience="staff" key="staff" />,
      },
      {
        name: "Settings",
        layout: "/admin",
        path: "/staff/settings",
        component: <AssessmentSettings audience="staff" key="staff" />,
      },
    ],
  },
  {
    name: "Volunteers",
    section: "People & Teams",
    icon: <VolunteersIcon />,
    layout: "/admin",
    path: "/volunteers",
    component: <AssessmentWeeklyCheckIn audience="volunteer" key="volunteer" />,
    collapse: true,
    items: [
      {
        name: "Weekly Check-In",
        layout: "/admin",
        path: "/volunteers",
        component: <AssessmentWeeklyCheckIn audience="volunteer" key="volunteer" />,
      },
      {
        name: "Assessment",
        layout: "/admin",
        path: "/volunteers/volunteer-assessment",
        component: <AssessmentOverview audience="volunteer" key="volunteer" />,
      },
      {
        name: "Profiles",
        layout: "/admin",
        path: "/volunteers/people",
        component: <AssessmentProfiles audience="volunteer" key="volunteer" />,
      },
      {
        name: "Profile Details",
        layout: "/admin",
        path: "/volunteers/people/:id",
        component: <AssessmentProfileDetails audience="volunteer" key="volunteer" />,
        hideInSidebar: true,
      },
      {
        name: "Role Fit",
        layout: "/admin",
        path: "/volunteers/role-fit",
        component: <AssessmentRoleFit audience="volunteer" key="volunteer" />,
      },
      {
        name: "Settings",
        layout: "/admin",
        path: "/volunteers/settings",
        component: <AssessmentSettings audience="volunteer" key="volunteer" />,
      },
    ],
  },

  // ── Engagement ──
  // Public link hub, visitor intake and giving
  {
    name: "Connect",
    section: "Engagement",
    icon: <ConnectIcon />,
    layout: "/admin",
    path: "/connect",
    component: <ConnectDashboard />,
    collapse: true,
    items: [
      {
        name: "Links",
        layout: "/admin",
        path: "/connect/links",
        component: <LinksManagement />,
      },
      {
        name: "Social Links",
        layout: "/admin",
        path: "/connect/social",
        component: <SocialLinksManagement />,
      },
      {
        name: "QR Code",
        layout: "/admin",
        path: "/connect/qr-code",
        component: <QRCodeGenerator />,
      },
      {
        name: "Customize",
        layout: "/admin",
        path: "/connect/customize",
        component: <CustomizeProfile />,
      },
      {
        name: "Visitors",
        layout: "/admin",
        path: "/connect/visitors",
        component: <VisitorSubmissions />,
      },
      {
        name: "Prayer Requests",
        layout: "/admin",
        path: "/connect/prayers",
        component: <PrayerRequests />,
      },
      {
        name: "Donations",
        layout: "/admin",
        path: "/connect/donations",
        component: <DonationsAnalytics />,
      },
      {
        name: "Giving Funds",
        layout: "/admin",
        path: "/connect/funds",
        component: <FundsManagement />,
      },
      {
        name: "Analytics",
        layout: "/admin",
        path: "/connect/analytics",
        component: <ConnectAnalytics />,
      },
    ],
  },
  {
    name: "Scheduling",
    section: "Engagement",
    icon: <SchedulingIcon />,
    layout: "/admin",
    path: "/service-scheduling",
    component: <ServiceScheduler />,
    collapse: true,
    items: [
      // Static path listed before `:id` so the breadcrumb trail (first match wins) names it.
      {
        name: "New Service",
        layout: "/admin",
        path: "/service-scheduling/services/new",
        component: <ServiceScheduler />,
        hideInSidebar: true,
      },
      {
        name: "Service Details",
        layout: "/admin",
        path: "/service-scheduling/services/:id",
        component: <ServiceScheduler />,
        hideInSidebar: true,
      },
      {
        name: "Edit Service",
        layout: "/admin",
        path: "/service-scheduling/services/:id/edit",
        component: <ServiceScheduler />,
        hideInSidebar: true,
      },
    ],
  },
  {
    name: "Attendance",
    section: "Engagement",
    icon: <AttendanceIcon />,
    layout: "/admin",
    path: "/attendance",
    component: <AttendanceReports />,
    collapse: true,
    items: [
      {
        name: "Reports",
        layout: "/admin",
        path: "/attendance/reports",
        component: <AttendanceReports />,
      },
      {
        name: "Services",
        layout: "/admin",
        path: "/attendance/services",
        component: <AttendanceServiceList />,
      },
      {
        name: "Charts",
        layout: "/admin",
        path: "/attendance/charts",
        component: <AttendanceCharts />,
      },
      {
        name: "Entry Form",
        layout: "/admin",
        path: "/attendance/entry/:id",
        component: <AttendanceEntry />,
        hideInSidebar: true,
      },
    ],
  },
  {
    name: "Documents",
    section: "Engagement",
    icon: <DocumentsIcon />,
    layout: "/admin",
    path: "/documents",
    component: <DocumentsPage />,
  },

  // ── Admin ──
  {
    name: "Settings",
    section: "Admin",
    icon: <SettingsIcon />,
    layout: "/admin",
    path: "/settings",
    component: <Settings />,
  },
  {
    name: "Planning Center Integration",
    layout: "/admin",
    path: "/pc-integration-redirecting",
    component: <PCIntegrationRedirecting />,
    hideInSidebar: true,
  },

  // ── Other layouts ──
  {
    name: "Signin",
    layout: "/auth",
    path: "/signin",
    hideInSidebar: true,
    component: <SignIn />,
  },
  {
    name: "AuthCallback",
    layout: "/auth",
    path: "/callback",
    hideInSidebar: true,
    component: <AuthCallback />,
  },
  {
    name: "Services",
    layout: "/volunteer",
    path: "/my-services",
    hideInSidebar: true,
    component: <VolunteerServices />,
  },
  {
    name: "Service Details",
    layout: "/volunteer",
    path: "/services/:id",
    hideInSidebar: true,
    component: <VolunteerServiceDetails />,
  },
  {
    name: "Check-in",
    layout: "/member",
    path: "/check-in",
    hideInSidebar: true,
    component: <CheckIn />,
  },
  {
    name: "Check-out QR Code",
    layout: "/member",
    path: "/check-out",
    hideInSidebar: true,
    component: <CheckOut />,
  },
  {
    name: "Registration",
    layout: "/member",
    path: "/register",
    hideInSidebar: true,
    component: <Registration />,
  },
  {
    name: "Link Hub",
    layout: "/link-hub",
    path: "/:slug",
    component: <PublicLinkHub />,
    hideInSidebar: true,
  },
];
